import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  HeartRateIcon,
  BreathingRateIcon,
  HRVIcon,
  BloodPressureIcon,
  OxygenSaturationIcon,
  StressIndexIcon,
  WellnessScoreIcon,
  HemoglobinIcon,
} from "./vitalIcons";
import { EMPTY_VITALS, generateMockVitals, jitterTowards } from "./mockVitals";
import { useSection } from "../../../i18n/useSection";
import "./vitals.scss";

// Mock timings. The native SDK measures for 60s; the mock keeps the same
// look-and-feel but shortens the wait so it stays a pleasant demo.
const PREP_SECONDS = 3;
const MEASURE_SECONDS = 8;

const GUIDELINE_IDLE = "rgba(255, 255, 255, 0.75)";
const GUIDELINE_GOOD = "#53ac82"; // Oron green — signals a locked-on reading.

/** Single metric tile in the results grid. */
const MetricCard = ({ icon, label, description, value, unit }) => (
  <div className="vm-card">
    <div className="vm-card-icon">{icon}</div>
    <div className="vm-card-label">{label}</div>
    {description ? <div className="vm-card-desc">{description}</div> : null}
    <div className="vm-card-value">{value}</div>
    {unit ? <div className="vm-card-unit">{unit}</div> : null}
  </div>
);

const BackArrow = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M19 12H5m0 0l7-7m-7 7l7 7"
      stroke="#1a1a1a"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Mock of the native Oron "Capture Vitals" screen.
 *
 * Reproduces the INTRO → CAPTURE → RESULTS flow. The capture step is faked:
 * a live camera preview (when available) plays behind an oval guide while a
 * countdown runs, then random-but-realistic metrics are revealed.
 */
const VitalsMock = ({ onClose, onSave }) => {
  const t = useSection("vitals");
  const [viewMode, setViewMode] = useState("INTRO"); // INTRO | CAPTURE | RESULTS
  const [hasStarted, setHasStarted] = useState(false); // false = prep, true = measuring
  const [remainingSeconds, setRemainingSeconds] = useState(MEASURE_SECONDS);
  const [guidelineColor, setGuidelineColor] = useState(GUIDELINE_IDLE);
  const [metrics, setMetrics] = useState(EMPTY_VITALS);
  const [cameraReady, setCameraReady] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const targetRef = useRef(null); // final metrics, revealed at the end
  const timersRef = useRef([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => clearInterval(id) || clearTimeout(id));
    timersRef.current = [];
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
  }, []);

  // Try to show the real webcam behind the oval — purely cosmetic. If it is
  // unavailable or denied, the flow continues with a placeholder.
  const startCamera = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) return;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraReady(true);
    } catch {
      // Camera denied/unavailable — mock still works with the placeholder.
      setCameraReady(false);
    }
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      clearTimers();
      stopCamera();
    };
  }, [clearTimers, stopCamera]);

  const beginMeasuring = useCallback(() => {
    setHasStarted(true);
    setGuidelineColor(GUIDELINE_GOOD);

    const target = generateMockVitals();
    targetRef.current = target;

    let elapsed = 0;
    // Tick the countdown + jittered live metrics every second.
    const tick = setInterval(() => {
      elapsed += 1;
      const remaining = MEASURE_SECONDS - elapsed;
      const progress = elapsed / MEASURE_SECONDS;
      setMetrics(jitterTowards(target, progress));
      setRemainingSeconds(Math.max(0, remaining));

      if (remaining <= 0) {
        clearInterval(tick);
        setMetrics(target);
        stopCamera();
        setViewMode("RESULTS");
      }
    }, 1000);
    timersRef.current.push(tick);
  }, [stopCamera]);

  const startFlow = useCallback(() => {
    clearTimers();
    setMetrics(EMPTY_VITALS);
    setHasStarted(false);
    setRemainingSeconds(MEASURE_SECONDS);
    setGuidelineColor(GUIDELINE_IDLE);
    setViewMode("CAPTURE");
    startCamera();

    // Short "preparing your camera" phase before the countdown begins.
    const prep = setTimeout(beginMeasuring, PREP_SECONDS * 1000);
    timersRef.current.push(prep);
  }, [beginMeasuring, clearTimers, startCamera]);

  const handleRetake = useCallback(() => {
    clearTimers();
    stopCamera();
    setMetrics(EMPTY_VITALS);
    setViewMode("INTRO");
  }, [clearTimers, stopCamera]);

  const handleClose = useCallback(() => {
    clearTimers();
    stopCamera();
    onClose?.();
  }, [clearTimers, stopCamera, onClose]);

  const handleSave = useCallback(() => {
    clearTimers();
    stopCamera();
    onSave?.(metrics);
  }, [clearTimers, stopCamera, onSave, metrics]);

  const headerTitle =
    viewMode === "INTRO"
      ? t.title
      : viewMode === "CAPTURE"
        ? t.measuring_title
        : t.results_title;

  const renderIntro = () => (
    <div className="vm-intro">
      <div className="vm-intro-illustration" aria-hidden>
        <HeartRateIcon width={72} height={66} color="#054997" />
      </div>
      <h2 className="vm-intro-title">{t.ready_title}</h2>
      <p className="vm-intro-desc">{t.ready_description}</p>
      <button
        type="button"
        className="vm-btn vm-btn-primary"
        onClick={startFlow}
      >
        {t.start}
      </button>
    </div>
  );

  const renderCapture = () => (
    <div className="vm-capture">
      <div className="vm-preview">
        <video
          ref={videoRef}
          className={`vm-preview-video ${cameraReady ? "is-live" : ""}`}
          autoPlay
          playsInline
          muted
        />
        {!cameraReady ? <div className="vm-preview-placeholder" /> : null}
        <div className="vm-guideline" style={{ borderColor: guidelineColor }} />
      </div>

      <div className="vm-capture-body">
        <div className="vm-capture-title">{t.measuring}</div>

        {!hasStarted ? (
          <>
            <div className="vm-spinner" aria-hidden />
            <p className="vm-prep-text">{t.preparing}</p>
            <p className="vm-instruction">{t.look_at_camera}</p>
          </>
        ) : (
          <>
            <div className="vm-timer">{remainingSeconds}</div>
            <div className="vm-timer-sub">{t.seconds_remaining}</div>
            <p className="vm-instruction">{t.stay_in_oval}</p>
          </>
        )}
      </div>
    </div>
  );

  const renderResults = () => (
    <div className="vm-results">
      <div className="vm-grid">
        <MetricCard
          icon={<HeartRateIcon width={42} height={38} />}
          label={t.heart_rate}
          description={t.heart_rate_description}
          value={`${metrics.hr}`}
          unit="bpm"
        />
        <MetricCard
          icon={<BloodPressureIcon width={50} height={50} />}
          label={t.blood_pressure}
          description={t.blood_pressure_description}
          value={`${metrics.sbp} / ${metrics.dbp}`}
          unit="mmHg"
        />
        <MetricCard
          icon={<BreathingRateIcon width={50} height={50} />}
          label={t.breathing_rate}
          description={t.breathing_rate_description}
          value={`${metrics.rr}`}
          unit="breaths/min"
        />
        <MetricCard
          icon={<OxygenSaturationIcon width={42} height={38} />}
          label={t.oxygen_saturation}
          description={t.oxygen_saturation_description}
          value={`${metrics.spo2}`}
          unit="%"
        />
        <MetricCard
          icon={<HRVIcon width={42} height={38} />}
          label={t.heart_rate_variability}
          description={t.heart_rate_variability_description}
          value={`${metrics.hrv_sdnn}`}
          unit="ms"
        />
        <MetricCard
          icon={<StressIndexIcon width={46} height={50} />}
          label={t.stress_index}
          description={t.stress_index_description}
          value={`${metrics.stress}`}
          unit=""
        />
        <MetricCard
          icon={<WellnessScoreIcon width={50} height={50} />}
          label={t.wellness_score}
          description={t.wellness_score_description}
          value={`${metrics.wellness}`}
          unit=""
        />
        <MetricCard
          icon={<HemoglobinIcon width={44} height={34} />}
          label={t.hemoglobin}
          description={t.hemoglobin_description}
          value={`${metrics.hemoglobin} / ${metrics.hba1c}`}
          unit="g/dL / %"
        />
      </div>

      <div className="vm-actions">
        <button
          type="button"
          className="vm-btn vm-btn-outline"
          onClick={handleRetake}
        >
          {t.retake}
        </button>
        <button
          type="button"
          className="vm-btn vm-btn-primary"
          onClick={handleSave}
        >
          {t.save}
        </button>
      </div>
    </div>
  );

  return (
    <div className="vitals-mock">
      <header className="vm-header">
        <button
          type="button"
          className="vm-back"
          onClick={handleClose}
          aria-label={t.back || "Back"}
        >
          <BackArrow />
        </button>
        <h1 className="vm-header-title">{headerTitle}</h1>
        <span className="vm-back" aria-hidden />
      </header>

      {viewMode === "INTRO"
        ? renderIntro()
        : viewMode === "CAPTURE"
          ? renderCapture()
          : renderResults()}
    </div>
  );
};

export default VitalsMock;
